import { AxiosError } from 'axios';

import { trackError, ErrorCategory, ErrorSeverity } from 'utils/analytics';
import { Logger } from 'utils/logger';

/**
 * Standard API error response structure from our backend
 */
/** Single validation error from FastAPI / Pydantic. */
interface ValidationErrorItem {
  msg: string;
  loc?: (string | number)[];
  type?: string;
}

interface ApiErrorResponse {
  message?: string;
  error?: string;
  detail?: string | ValidationErrorItem[];
  errors?: Array<{ message?: string }>;
}

/**
 * Options for handling errors
 */
interface HandleErrorOptions {
  /** Context description for logging (e.g., 'Failed to fetch users') */
  context: string;
  /** Whether to log the error to console via Logger.error */
  logError?: boolean;
  /** Whether to track error in analytics */
  trackInAnalytics?: boolean;
  /** Error category for analytics */
  category?: ErrorCategory;
  /** Error severity for analytics */
  severity?: ErrorSeverity;
  /** Additional context for analytics */
  analyticsContext?: Record<string, unknown>;
}

/**
 * Type guard to check if an error is an AxiosError
 */
export function isAxiosError(
  error: unknown
): error is AxiosError<ApiErrorResponse> {
  return (
    error !== null &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Extracts a user-friendly error message from various error types.
 * Handles Axios errors, standard Error objects, and unknown error types.
 *
 * @param error - The error to extract message from
 * @param fallbackMessage - Default message if no error message can be extracted
 * @returns A user-friendly error message string
 *
 * @example
 * ```ts
 * try {
 *   await api.post('/endpoint');
 * } catch (error) {
 *   const message = getErrorMessage(error, 'Failed to save data');
 *   notification.error({ message });
 * }
 * ```
 */
export function getErrorMessage(
  error: unknown,
  fallbackMessage = 'An unexpected error occurred'
): string {
  // Handle Axios errors (most common in API hooks)
  if (isAxiosError(error)) {
    const responseData = error.response?.data;

    // Try various common error message locations in API responses
    if (responseData) {
      if (typeof responseData.message === 'string' && responseData.message) {
        return responseData.message;
      }
      if (typeof responseData.error === 'string' && responseData.error) {
        return responseData.error;
      }
      if (typeof responseData.detail === 'string' && responseData.detail) {
        return responseData.detail;
      }
      // FastAPI / Pydantic validation errors: detail is an array of objects
      if (Array.isArray(responseData.detail) && responseData.detail.length) {
        return (responseData.detail as ValidationErrorItem[])
          .map((e) => {
            const field = e.loc?.filter((l) => l !== 'body').join('.') ?? '';
            return field ? `${field}: ${e.msg}` : e.msg;
          })
          .join('; ');
      }
      if (
        Array.isArray(responseData.errors) &&
        responseData.errors[0]?.message
      ) {
        return responseData.errors[0].message;
      }
    }

    // Fall back to axios error message
    if (error.message) {
      return error.message;
    }
  }

  // Handle standard Error objects
  if (error instanceof Error && error.message) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string' && error) {
    return error;
  }

  // Handle objects with message property
  if (error !== null && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === 'string' && msg) {
      return msg;
    }
  }

  return fallbackMessage;
}

/**
 * Determines the error category based on the error type and HTTP status code.
 *
 * @param error - The error to categorize
 * @returns The appropriate ErrorCategory
 */
export function categorizeApiError(error: unknown): ErrorCategory {
  if (!isAxiosError(error)) {
    return ErrorCategory.RUNTIME;
  }

  const status = error.response?.status;

  if (!status) {
    // No response means network error
    if (
      error.code === 'ECONNABORTED' ||
      error.message?.toLowerCase().includes('timeout')
    ) {
      return ErrorCategory.NETWORK;
    }
    if (error.message === 'Network Error') {
      return ErrorCategory.NETWORK;
    }
    return ErrorCategory.NETWORK;
  }

  if (status === 401 || status === 403) {
    return ErrorCategory.AUTHENTICATION;
  }

  if (status === 400 || status === 422) {
    return ErrorCategory.VALIDATION;
  }

  return ErrorCategory.API;
}

/**
 * Determines the error severity based on the error type and HTTP status code.
 *
 * @param error - The error to evaluate
 * @returns The appropriate ErrorSeverity
 */
export function getApiErrorSeverity(error: unknown): ErrorSeverity {
  if (!isAxiosError(error)) {
    return ErrorSeverity.MEDIUM;
  }

  const status = error.response?.status;

  if (!status) {
    // Network errors are critical
    return ErrorSeverity.CRITICAL;
  }

  if (status === 401 || status === 403) {
    return ErrorSeverity.HIGH;
  }

  if (status >= 500) {
    return ErrorSeverity.CRITICAL;
  }

  if (status === 429) {
    return ErrorSeverity.HIGH;
  }

  if (status === 400 || status === 422) {
    return ErrorSeverity.LOW;
  }

  if (status === 404) {
    return ErrorSeverity.MEDIUM;
  }

  return ErrorSeverity.MEDIUM;
}

/**
 * Handles errors consistently across the application.
 * Logs errors, tracks them in analytics, and returns a user-friendly message.
 *
 * @param error - The error to handle
 * @param options - Options for error handling
 * @returns A user-friendly error message
 *
 * @example
 * ```ts
 * // In a mutation's onError callback
 * onError: (error) => {
 *   const message = handleError(error, {
 *     context: 'Failed to save customer',
 *     trackInAnalytics: true,
 *   });
 *   notification.error({ message });
 * }
 * ```
 *
 * @example
 * ```ts
 * // In a try-catch block
 * try {
 *   await api.post('/endpoint', data);
 * } catch (error) {
 *   const message = handleError(error, {
 *     context: 'Failed to submit form',
 *     logError: true,
 *     trackInAnalytics: true,
 *     analyticsContext: { formType: 'customer' },
 *   });
 *   notification.error({ message });
 * }
 * ```
 */
export function handleError(
  error: unknown,
  options: HandleErrorOptions
): string {
  const {
    context,
    logError = true,
    trackInAnalytics = true,
    category,
    severity,
    analyticsContext,
  } = options;

  const errorMessage = getErrorMessage(error, context);

  // Log error to console
  if (logError) {
    Logger.error(`${context}:`, error);
  }

  // Track in analytics
  if (trackInAnalytics) {
    const autoCategory = category ?? categorizeApiError(error);
    const autoSeverity = severity ?? getApiErrorSeverity(error);

    const isAxiosErrorInstance = isAxiosError(error);

    trackError({
      message: `${context}: ${errorMessage}`,
      error,
      category: autoCategory,
      severity: autoSeverity,
      context: {
        ...analyticsContext,
        errorContext: context,
        endpoint: isAxiosErrorInstance ? error.config?.url : undefined,
        method: isAxiosErrorInstance
          ? error.config?.method?.toUpperCase()
          : undefined,
        status: isAxiosErrorInstance ? error.response?.status : undefined,
      },
    });
  }

  return errorMessage;
}

/**
 * Creates an error handler function pre-configured with options.
 * Useful for creating consistent error handlers in hooks.
 *
 * @param baseContext - Base context string for all errors
 * @param baseOptions - Default options for all error handling
 * @returns A function that handles errors with the pre-configured options
 *
 * @example
 * ```ts
 * // Create a hook-specific error handler
 * const handleMutationError = createErrorHandler('Customer API', {
 *   trackInAnalytics: true,
 * });
 *
 * // Use in mutations
 * onError: (error) => {
 *   const message = handleMutationError(error, 'Failed to create customer');
 *   notification.error({ message });
 * }
 * ```
 */
export function createErrorHandler(
  baseContext: string,
  baseOptions: Omit<HandleErrorOptions, 'context'> = {}
) {
  return (error: unknown, actionContext: string): string => {
    return handleError(error, {
      ...baseOptions,
      context: `${baseContext} - ${actionContext}`,
    });
  };
}

/**
 * Type guard to check if an error has a response with a specific status code
 *
 * @param error - The error to check
 * @param status - The HTTP status code to check for
 * @returns true if the error has the specified status code
 */
export function hasStatusCode(error: unknown, status: number): boolean {
  return isAxiosError(error) && error.response?.status === status;
}

/**
 * Checks if an error is a network error (no response received)
 */
export function isNetworkError(error: unknown): boolean {
  if (!isAxiosError(error)) {
    return false;
  }

  return (
    !error.response &&
    (error.code === 'ECONNABORTED' ||
      error.message === 'Network Error' ||
      error.message?.toLowerCase().includes('timeout'))
  );
}

/**
 * Checks if an error is an authentication error (401 or 403)
 */
export function isAuthError(error: unknown): boolean {
  return hasStatusCode(error, 401) || hasStatusCode(error, 403);
}

/**
 * Checks if an error is a server error (5xx)
 */
export function isServerError(error: unknown): boolean {
  if (!isAxiosError(error)) {
    return false;
  }
  const status = error.response?.status;
  return status !== undefined && status >= 500 && status < 600;
}

/**
 * Checks if an error is a validation error (400 or 422)
 */
export function isValidationError(error: unknown): boolean {
  return hasStatusCode(error, 400) || hasStatusCode(error, 422);
}

/**
 * Checks if an error is a "not found" error (404)
 */
export function isNotFoundError(error: unknown): boolean {
  return hasStatusCode(error, 404);
}

/**
 * Checks if an error is a rate limit error (429)
 */
export function isRateLimitError(error: unknown): boolean {
  return hasStatusCode(error, 429);
}

/**
 * Extracts all error properties for analytics tracking.
 * Consolidates error message, category, severity, and HTTP details into a single object.
 *
 * @param error - The error to extract properties from
 * @returns An object containing all error properties for tracking
 *
 * @example
 * ```ts
 * const errorProperties = getErrorProperties(error);
 * trackEvent('API_ERROR', errorProperties);
 * ```
 */
export function getErrorProperties(error: unknown): Record<string, unknown> {
  const baseProperties = {
    errorMessage: getErrorMessage(error),
    errorCategory: categorizeApiError(error),
    errorSeverity: getApiErrorSeverity(error),
  };

  if (isAxiosError(error)) {
    return {
      ...baseProperties,
      httpStatus: error.response?.status,
      endpoint: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      isNetworkError: isNetworkError(error),
      isAuthError: isAuthError(error),
      isServerError: isServerError(error),
      isValidationError: isValidationError(error),
    };
  }

  return baseProperties;
}
